import { ref, watch, computed, nextTick, inject, onBeforeUnmount } from "vue";
import { formInputInjection } from "../types/keys.mjs";
import { useValidation } from "./useValidation.mjs";
import { useSizeable } from "./useSizeable.mjs";
import { deepEqual } from "@spoferan/spoferan-ts-core";
export const InputComponentEmits = ["update:modelValue", "update:valid", "update:error", "clear", "reset"];
export function useInputComponent(props, emit = () => {
}, options = {}) {
  const form = inject(formInputInjection, null);
  const inputId = (props.name ? `${props.name}-` : "") + Math.random().toString(36).slice(2);
  const submitValue = ref(options.initSubmitValue ? options.initSubmitValue(props) : props.modelValue);
  watch(submitValue, (newValue) => {
    emit("update:modelValue", newValue);
    hasInput.value = true;
    if (shouldValidate.value) {
      nextTick(validate);
    }
  });
  watch(() => props.modelValue, (newValue) => {
    submitValue.value = newValue;
  });
  const validationRules = computed(() => {
    return { ...options.baseValidationRules ? options.baseValidationRules.value : {}, ...props.rules };
  });
  watch(validationRules, (newValue, oldValue) => {
    if (deepEqual(newValue, oldValue)) {
      return;
    }
    validate();
  });
  const { validate, clearValidation, isValid, errorBag, setValidationError } = useValidation(options.validationValue ?? submitValue, validationRules, {
    type: options.validationType ?? props.type,
    valid: props.valid
  });
  watch(isValid, (newValue) => {
    emit("update:valid", newValue);
  });
  watch(() => props.valid, (newValue) => {
    isValid.value = newValue;
  });
  watch(errorBag, (newValue, oldValue) => {
    if (deepEqual(newValue, oldValue)) {
      return;
    }
    refreshValidationError();
  });
  const errorMessage = ref(props.error);
  watch(() => props.error, (newValue) => {
    errorMessage.value = newValue;
  });
  watch(errorMessage, (newValue) => {
    emit("update:error", newValue);
  });
  const hasError = computed(() => {
    return hasInput.value && !!errorMessage.value;
  });
  const isEmpty = computed(() => {
    return ([void 0, null].includes(submitValue.value) || typeof submitValue.value === "string" && !submitValue.value.length) && ([void 0, null].includes(props.placeholder) || typeof props.placeholder === "string" && !props.placeholder.length);
  });
  const hasInput = ref(false);
  const { sizeableString } = useSizeable(props);
  const isResetting = ref(false);
  watch(isResetting, (newValue) => {
    if (newValue) {
      nextTick(() => {
        hasInput.value = false;
        isResetting.value = false;
      });
    }
  });
  const shouldValidate = computed(() => {
    return !isResetting.value && hasInput.value;
  });
  const isRequired = computed(() => {
    return props.rules && !!props.rules.required;
  });
  function clearValue() {
    options.clearValue?.();
    submitValue.value = Array.isArray(submitValue.value) ? [] : null;
    emit("clear");
  }
  function reset() {
    isResetting.value = true;
    clearValue();
    clearValidation();
    emit("reset");
  }
  function refreshValidationError() {
    if (isValid.value) {
      errorMessage.value = null;
      form?.removeInputError(inputId);
    } else {
      errorMessage.value = Object.keys(errorBag.value).length ? Object.values(errorBag.value)[0] : null;
      form?.addInputError(inputId, {
        message: errorMessage.value,
        label: props.label ?? props.placeholder,
        name: props.name
      });
    }
  }
  form?.addInput({
    id: inputId,
    name: props.name,
    validate: (forceErrorDisplay = false) => {
      if (forceErrorDisplay) {
        hasInput.value = true;
      }
      return validate();
    },
    reset,
    clearValidation,
    setValidationError
  });
  validate();
  refreshValidationError();
  onBeforeUnmount(() => {
    form?.removeInput(inputId);
  });
  return {
    submitValue,
    isRequired,
    hasError,
    hasInput,
    errorMessage,
    sizeableString,
    isEmpty,
    shouldValidate,
    validate,
    reset,
    clearValue
  };
}
